import { Link } from "@tanstack/react-router";
import type {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  INumberCellEditorParams,
  IRichCellEditorParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import type { Decimal } from "decimal.js";
import {
  CircleSlashIcon,
  FileUpIcon,
  PanelRightIcon,
  PencilIcon,
  PlusIcon,
  XSquareIcon,
} from "lucide-react";
import React from "react";
import { Menu, MenuTrigger } from "react-aria-components";

import { getResultSample } from "@blis/shared/models/result";
import type { Prisma } from "@blis/shared/types/prisma";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { AgGridReact } from "@/components/grid/AgGrid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldError, Label, TextArea, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { DropdownItem, ListBox, ListBoxItem } from "@/components/ui/list-box";
import {
  MenuTrigger as CustomMenuTrigger,
  MenuItem,
  MenuSeparator,
} from "@/components/ui/menu";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import { Separator } from "@/components/ui/separator";
import { Sheet } from "@/components/ui/sheet";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { apiUtils } from "@/context/query";
import {
  AccessionDialogProvider,
  useSetDialogState,
} from "@/features/accession/components/detail/state";
import { UpdateResultDialog } from "@/features/accession/components/dialogs/UpdateResultDialog";
import { useFeatureFlags, useSettings } from "@/features/admin";
import { useAuth } from "@/features/auth/hooks";
import { usePrintLabel } from "@/lib/labels";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import type {
  WorkflowStep as TWorkflowStep,
  WorkspaceResultEntry,
  WorkspaceResultEntryPayload,
} from "../../types";
import { workspaceGridTheme } from "../../utils/grid";
import { useWorkspace } from "../WorkspaceProvider";
import { WorkflowHeader } from "./WorkflowHeader";

// Enhanced type for the interpretation grid that includes row state tracking
type EnhancedWorkspaceObservationSummary = WorkspaceResultEntry & {
  _userMode: "canEnter" | "canValidate" | "canViewOnly" | "cancelled";
  _originalResultId: string | null;
  _originalResultNumeric: import("decimal.js").Decimal | null;
  _originalResultFreeText: string | null;
  _originalAttributes: WorkspaceResultEntry["attributes"];
};

export function WorkflowResultEntry({ step }: { step: TWorkflowStep }) {
  const { activeDay, testRun, testSpec } = useWorkspace();
  const { data, isLoading } = trpc.process.getResultEntry.useQuery({
    testRunId: testRun.id,
    stepId: step.id,
    dayNumber: activeDay,
  });
  const cancelNotPerformed = trpc.process.cancelNotPerformed.useMutation(
    mutationResponse({
      errorTitle: "Failed to cancel results",
      successMessage: "Results cancelled as not performed",
    }),
  );
  let title = step.title;
  if (activeDay) {
    title = `${title} - Day ${activeDay}`;
  }
  const notPerformedMessage = React.useMemo(() => {
    if (!data) return "";
    const total = data.testOrderResults.length;
    const emptyCount = data.testOrderResults.filter(
      (r) => r.status === "empty",
    ).length;
    const skipped = total - emptyCount;
    if (skipped === 0) {
      return `Cancel all ${total} results as not performed?`;
    }
    if (skipped === 1) {
      return `${emptyCount} of ${total} results will be cancelled as not performed. The remaining result is already entered or cancelled and will not change.`;
    }
    return `${emptyCount} of ${total} results will be cancelled as not performed. The other ${skipped} are already entered or cancelled and will not change.`;
  }, [data]);

  return (
    <>
      <WorkflowHeader
        step={step}
        title={title}
        actions={
          activeDay &&
          hasEmpty(data) && (
            <ConfirmPrompt
              title="Cancel results"
              message={notPerformedMessage}
              loading={cancelNotPerformed.isPending}
              onConfirm={() => {
                cancelNotPerformed.mutate({
                  testRunId: testRun.id,
                  dayNumber: activeDay,
                });
              }}
            >
              <Button variant="outline">
                <CircleSlashIcon />
                Not performed
              </Button>
            </ConfirmPrompt>
          )
        }
      />
      <ResultEntryGrid step={step} data={data} isLoading={isLoading} />
    </>
  );
}

function ResultEntryGrid({
  step,
  data,
  isLoading,
}: {
  step: TWorkflowStep;
  data: WorkspaceResultEntryPayload | undefined;
  isLoading: boolean;
}) {
  const { user } = useAuth();
  const gridRef =
    React.useRef<AgGridReact<EnhancedWorkspaceObservationSummary>>(null);
  const [rowData, setRowData] = React.useState<
    EnhancedWorkspaceObservationSummary[]
  >([]);
  const { activeDay } = useWorkspace();
  const [changedIds, setChangedIds] = React.useState<Set<number>>(new Set());
  const [conflictDialogOpen, setConflictDialogOpen] = React.useState(false);
  const [conflicts, setConflicts] = React.useState<
    Array<{
      accessionId: number;
      resultName: string | null;
      enteredValue: string;
      expectedValue: string;
    }>
  >([]);
  const originalValues = React.useRef<
    Map<
      number,
      {
        resultId: string | null;
        resultNumeric: Decimal | null;
        resultFreeText: string | null;
        comment: string | null;
        sampleId: number | null;
        materialLotId: number | null;
        metadata: Prisma.JsonObject | null;
      }
    >
  >(new Map());
  const [rowId, setRowId] = React.useState<number | null>(null);
  const { testRun } = useWorkspace();

  const { data: organismResults } = trpc.results.organismResults.useQuery(
    undefined,
    {
      enabled: data?.testResultSpecs.some((t) => t.type === "ORGANISM"),
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const resultMetadata = React.useMemo(() => {
    if (!activeDay) return {};
    return {
      dayNumber: activeDay,
    };
  }, [activeDay]);

  const resultEntryStuff = data?.testOrderResults || [];
  const testResultSpecs = data?.testResultSpecs || [];

  const submitInterpretation = trpc.process.submitInterpretation.useMutation(
    mutationResponse({
      errorTitle: "Failed to submit interpretation",
      successMessage: "Interpretation submitted successfully",
      onSuccess: () => {
        setChangedIds(new Set());
        // The workspace data should be invalidated/refetched to update row states
        // This will cause the useEffect to run again with updated server data
      },
    }),
  );

  const hasResult = React.useCallback(
    (data?: EnhancedWorkspaceObservationSummary, original = true) => {
      if (!data) return false;
      if (original) {
        const orig = originalValues.current.get(data.id);
        return (
          !!orig?.resultId || !!orig?.resultNumeric || !!orig?.resultFreeText
        );
      }
      return !!data.resultId || !!data.resultNumeric || !!data.resultFreeText;
    },
    [],
  );

  const isPending = React.useCallback(
    (data?: EnhancedWorkspaceObservationSummary) => {
      if (!data) return false;
      return hasResult(data) && !!data.enteredById && !data.validatedById;
    },
    [hasResult],
  );

  const handleSave = React.useCallback(() => {
    if (!gridRef.current) return;

    type Change = Partial<Omit<EnhancedWorkspaceObservationSummary, "id">> & {
      id: number;
      accessionId: number;
      testOrderId: number;
      testResultSpecId: number;
      type: "TEXT" | "NUMERIC" | "FREE_TEXT" | "ORGANISM";
      status: "empty" | "initial" | "validated";
      metadata: Prisma.JsonObject | null;
    };
    const enterResultChanges: Change[] = [];
    const validateResultChanges: Change[] = [];
    const foundConflicts: Array<{
      accessionId: number;
      resultName: string | null;
      enteredValue: string;
      expectedValue: string;
    }> = [];

    changedIds.forEach((stableRowId) => {
      // Find the row data by stable row ID
      const data = rowData.find((row) => row.id === stableRowId);
      if (data) {
        // Derive sampleId if not directly assigned to the result
        const derivedSample = getResultSample(data, data.testOrder);
        const sampleId = data.sampleId || derivedSample?.id;

        const change: Change = {
          id: data.id,
          accessionId: data.testOrder.accessionId,
          testOrderId: data.testOrder.id,
          testResultSpecId: data.testResultSpecId,
          type: data.testResultSpec.type as "TEXT",
          resultId: data.resultId,
          resultNumeric: data.resultNumeric,
          resultFreeText: data.resultFreeText,
          comment: data.comment,
          attributes: data.attributes,
          sampleId: sampleId,
          materialLotId: data.materialLotId,
          status: "empty",
          metadata: {
            ...(data.metadata as Prisma.JsonObject | null),
            ...resultMetadata,
          },
        };

        // Use user mode to determine if this is validation or initial entry
        if (data._userMode === "canValidate") {
          // This is validation - check for conflicts using stored original values
          const type = data.testResultSpec.type;
          let isMatch = false;
          let enteredValue = "";
          let expectedValue = "";

          switch (type) {
            case "TEXT": {
              const originalResult = data.testResultSpec.testResults.find(
                (t) => t.testResult.value === data._originalResultId,
              );
              expectedValue = originalResult?.testResult.description || "";

              const enteredResult = data.testResultSpec.testResults.find(
                (t) => t.testResult.value === change.resultId,
              );
              enteredValue = enteredResult?.testResult.description || "";

              isMatch = change.resultId === data._originalResultId;
              break;
            }
            case "NUMERIC": {
              expectedValue = data._originalResultNumeric?.toString() || "";
              enteredValue = change.resultNumeric?.toString() || "";
              isMatch =
                data._originalResultNumeric?.equals(
                  change.resultNumeric || 0,
                ) ||
                (!data._originalResultNumeric && !change.resultNumeric);
              break;
            }
            case "ORGANISM":
            case "FREE_TEXT": {
              expectedValue = data._originalResultFreeText || "";
              enteredValue = change.resultFreeText || "";
              isMatch = data._originalResultFreeText === change.resultFreeText;
              break;
            }
          }

          if (!isMatch) {
            foundConflicts.push({
              accessionId: data.testOrder.accessionId,
              resultName: data.testResultSpec.name,
              enteredValue,
              expectedValue,
            });
          } else {
            change.status = "validated";
            validateResultChanges.push(change);
          }
        } else if (data._userMode === "canEnter") {
          // This is initial entry
          enterResultChanges.push(change);
        }
        // Note: canViewOnly mode changes are ignored (shouldn't happen since not editable)
      }
    });

    // If there are conflicts, show the dialog instead of submitting
    if (foundConflicts.length > 0) {
      setConflicts(foundConflicts);
      setConflictDialogOpen(true);
      return;
    }

    // Submit mixed changes in a single call
    if (enterResultChanges.length > 0 || validateResultChanges.length > 0) {
      submitInterpretation.mutate({
        stepId: step.id,
        testRunId: testRun.id,
        metadata: {},
        enterResults:
          enterResultChanges.length > 0 ? enterResultChanges : undefined,
        validateResults:
          validateResultChanges.length > 0 ? validateResultChanges : undefined,
      });
    }
  }, [
    changedIds,
    step.id,
    submitInterpretation.mutate,
    rowData,
    resultMetadata,
    testRun.id,
  ]);

  useTinyKeys(
    {
      "$mod+s": (e) => {
        e.preventDefault();
        handleSave();
      },
    },
    [handleSave],
  );

  const resultValueGetter = React.useCallback(
    (params: ValueGetterParams<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;
      if (!data) return null;
      const type = data.testResultSpec.type;

      // For validation mode with no value entered yet, show placeholder
      if (
        data._userMode === "canValidate" &&
        !data.resultId &&
        !data.resultFreeText &&
        (data.resultNumeric === null || data.resultNumeric === undefined)
      ) {
        return null;
      }

      switch (type) {
        case "TEXT":
          return (
            data.testResultSpec.testResults.find(
              (t) => t.testResult.value === data.resultId,
            )?.testResult ?? null
          );
        case "NUMERIC":
          return data.resultNumeric;
        case "ORGANISM":
        case "FREE_TEXT":
          return data.resultFreeText;
        default:
          return null;
      }
    },
    [],
  );

  const resultValueFormatter = React.useCallback(
    (params: ValueFormatterParams<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;
      if (!data) return "";

      // Show "Pending Validation" for validation rows with no entered value yet
      if (data._userMode === "canValidate" && !changedIds.has(data.id)) {
        return "";
      }

      const value = params.value;
      if (value === null || value === undefined) return "";
      const type = data?.testResultSpec.type;
      if (type === "TEXT") {
        return value.description || "";
      }
      if (type === "NUMERIC") {
        return value;
      }
      if (type === "FREE_TEXT" || type === "ORGANISM") {
        return value || "";
      }
      return value;
    },
    [changedIds],
  );

  const resultValueSetter = React.useCallback(
    (params: ValueSetterParams<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;
      const newValue = params.newValue;
      if (!data) return false;

      const type = data.testResultSpec.type;

      // Simply update the data based on the type
      switch (type) {
        case "TEXT": {
          const selectedResult = data.testResultSpec.testResults.find(
            (t) => t.testResult.description === newValue,
          );
          if (selectedResult) {
            data.resultId = selectedResult.testResult.value;
            return true;
          }
          return false;
        }
        case "NUMERIC":
          data.resultNumeric = newValue ?? null;
          return true;
        case "ORGANISM":
        case "FREE_TEXT":
          data.resultFreeText = newValue ?? null;
          return true;
        default:
          return false;
      }
    },
    [],
  );

  const { featureFlags } = useFeatureFlags();

  React.useEffect(() => {
    const enhancedData = resultEntryStuff.map((os, index) => {
      if (os.testOrder.cancelled || os.status === "cancelled") {
        return {
          ...os,
          _userMode: "cancelled" as const,
          _originalResultId: os.finalResultId || os.resultId,
          _originalResultNumeric: os.finalResultNumeric || os.resultNumeric,
          _originalResultFreeText: os.finalResultFreeText || os.resultFreeText,
          _originalAttributes: os.attributes,
        };
      }
      // Ensure proper user ID comparison
      const currentUserId = user.profile.id;
      const enteredByUserId = os.enteredById || null;

      // Determine what the current user can do with this result
      let userMode: "canEnter" | "canValidate" | "canViewOnly" | "cancelled";
      if (os.status === "empty") {
        userMode = "canEnter";
      } else if (
        os.status === "initial" &&
        (enteredByUserId !== currentUserId ||
          featureFlags?.allowSelfValidation ||
          os.testResultSpec.disableDoubleEntry)
      ) {
        userMode = "canValidate";
      } else {
        // Either validated, or pending but current user entered it
        userMode = "canViewOnly";
      }

      return {
        ...os,
        // Add explicit state tracking
        _userMode: userMode,
        _originalResultId: os.finalResultId || os.resultId,
        _originalResultNumeric: os.finalResultNumeric || os.resultNumeric,
        _originalResultFreeText: os.finalResultFreeText || os.resultFreeText,
        _originalAttributes: os.attributes,
        // Only clear values for validation mode, not for view-only mode
        resultId:
          userMode === "canValidate" ? null : os.finalResultId || os.resultId,
        resultNumeric:
          userMode === "canValidate"
            ? null
            : os.finalResultNumeric || os.resultNumeric,
        resultFreeText:
          userMode === "canValidate"
            ? null
            : os.finalResultFreeText || os.resultFreeText,
        attributes: userMode === "canValidate" ? [] : os.attributes,
      };
    });

    setRowData(enhancedData);

    // Store original values for comparison during save using stable row IDs
    originalValues.current = new Map(
      enhancedData.map((os) => [
        os.id,
        {
          resultId: os._originalResultId,
          resultNumeric: os._originalResultNumeric,
          resultFreeText: os._originalResultFreeText,
          comment: os.comment,
          sampleId: os.sampleId,
          materialLotId: os.materialLotId,
          metadata: os.metadata as Prisma.JsonObject | null,
        },
      ]),
    );
  }, [resultEntryStuff, user.profile.id, featureFlags?.allowSelfValidation]);

  const isEditable = React.useCallback(
    (params: EditableCallbackParams<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;
      if (!data) return false;

      if (data._userMode === "cancelled") {
        return false;
      }

      // Additional safeguard: if user has made changes to this row, they can't validate it
      // if (changedIds.has(data.id) && data._userMode === "canValidate") {
      //   return false;
      // }

      // Allow editing for initial entry or validation mode
      return data._userMode === "canEnter" || data._userMode === "canValidate";
    },
    [],
  );

  const isCommentEditable = React.useCallback(
    (params: EditableCallbackParams<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;
      if (!data) return false;
      // Only allow comment editing during initial entry
      return data._userMode === "canEnter";
    },
    [],
  );

  const handleCellValueChanged = React.useCallback(
    (params: CellValueChangedEvent<EnhancedWorkspaceObservationSummary>) => {
      const data = params.data;

      // if the value is the same as the original value, don't mark it as changed, or remove it from the changedIds set
      if (
        data.status !== "initial" &&
        data.resultId === data._originalResultId &&
        data.resultNumeric === data._originalResultNumeric &&
        data.resultFreeText === data._originalResultFreeText
      ) {
        setChangedIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(data.id);
          return newSet;
        });
        return;
      }

      // Simply mark this row as changed - row state will only update after successful submission
      setChangedIds((prev) => new Set([...prev, data.id]));
    },
    [],
  );

  const attributeColumnDefs = React.useMemo(() => {
    if (!testResultSpecs || testResultSpecs.length === 0) return [];
    return testResultSpecs.flatMap((spec) =>
      spec.attributeSpecs.map((spec) => ({
        colId: `attributes.${spec.code}`,
        headerName: spec.name,
        editable: isEditable,
        cellEditorSelector: (params) => {
          switch (spec.type) {
            case "TEXT":
              return {
                component: "agTextCellEditor",
                // params: {
                //   value: params.data?.attributes.find((a) => a.attributeSpec.code === spec.code)?.textValue || "",
                // },
              };
            case "NUMERIC":
              return {
                component: "agNumberCellEditor",
                params: {
                  min: 0,
                  preventStepping: true,
                } as INumberCellEditorParams,
              };
            case "BOOLEAN":
              return {
                component: "agCheckboxCellEditor",
              };
            default:
              return {
                component: "agTextCellEditor",
              };
          }
        },
        valueGetter: (params) => {
          const data = params.data;
          if (!data) return null;
          const attribute = data.attributes.find(
            (a) => a.attributeSpec.code === spec.code,
          );
          if (!attribute) return null;
          if (spec.type === "TEXT") {
            return attribute.textValue;
          }
          if (spec.type === "NUMERIC") {
            return attribute.numericValue;
          }
          if (spec.type === "BOOLEAN") {
            return attribute.booleanValue;
          }
          return null;
        },
        valueFormatter: (params) => {
          const data = params.data;
          if (!data) return "";
          const attribute = data.attributes.find(
            (a) => a.attributeSpec.code === spec.code,
          );
          if (!attribute) return "";
          if (spec.type === "TEXT") {
            return attribute.textValue || "";
          }
          if (spec.type === "NUMERIC") {
            return attribute.numericValue;
          }
          if (spec.type === "BOOLEAN") {
            return attribute.booleanValue ? "Yes" : "No";
          }
          return "";
        },
        valueSetter: (params) => {
          const data = params.data;
          if (!data) return false;
          const attribute = data.attributes.find(
            (a) => a.attributeSpec.code === spec.code,
          ) || {
            attributeSpecId: spec.id,
            attributeSpec: spec,
            textValue: null,
            booleanValue: null,
            numericValue: null,
          };
          if (spec.type === "TEXT") {
            attribute.textValue = params.newValue;
          } else if (spec.type === "NUMERIC") {
            attribute.numericValue = params.newValue;
          } else if (spec.type === "BOOLEAN") {
            attribute.booleanValue = params.newValue;
          }
          data.attributes = [attribute];
          return true;
        },
        cellClass: "tabular-nums",
        minWidth: 120,
        maxWidth: 140,
      })),
    );
  }, [isEditable, testResultSpecs]);

  const colDefs = React.useMemo(() => {
    return [
      {
        colId: "leftAction",
        headerName: "",
        maxWidth: 34,
        pinned: "left",
        cellStyle: { padding: 0 },
        cellRenderer: (params) => {
          return (
            <Button
              size="icon-sm"
              variant="ghost"
              className="w-full h-full rounded-none text-muted-foreground"
              onPress={() => setRowId(params.data.id)}
            >
              <PanelRightIcon />
            </Button>
          );
        },
        suppressHeaderMenuButton: true,
        resizable: false,
      },
      {
        field: "testOrder.accessionId",
        headerName: "Accession",
        cellRenderer: (params) => {
          const data = params.data;
          if (!data) return null;
          return (
            <Link
              to="/accessions/$id"
              params={{ id: params.data.testOrder.accessionId }}
            >
              {params.data.testOrder.accessionId}
            </Link>
          );
        },
        // cellRenderer: "agGroupCellRenderer",
        cellClass: "tabular-nums",
        minWidth: 120,
        maxWidth: 140,
      },
      // { field: "accession.subject.caseId", headerName: "Subject" },
      { field: "testResultSpec.name", headerName: "Name" },
      {
        field: "sample.displayId",
        headerName: "Sample",
        valueGetter: (params) => {
          if (!params.data) return "";
          const sample = getResultSample(params.data, params.data.testOrder);
          return sample?.displayId || "";
        },
      },
      {
        field: "materialLot.materialSpec.name",
        headerName: "Media",
        // cellRenderer: (params) => {
        //   const data = params.data;
        // },
      },
      {
        colId: "status",
        headerName: "Status",
        width: 160,
        cellClass: "overflow-hidden min-w-0",
        valueGetter: (params) => {
          if (!params.data) return "";
          const data = params.data;

          if (data._userMode === "cancelled") {
            return "Cancelled";
          }

          if (data.status === "empty") {
            return "Not Entered";
          }
          if (data.status === "initial") {
            if (data._userMode === "canValidate") {
              return "Needs Validation";
            }
            return "Pending Validation";
          }
          return "Validated";
        },
        cellRenderer: (params) => {
          const data = params.data;
          if (!data) return null;
          const status = params.value;

          if (data._userMode === "cancelled") {
            return (
              <Badge size="sm" color="red">
                Cancelled
              </Badge>
            );
          }

          if (status === "Not Entered") {
            return (
              <Badge size="sm" color="gray">
                Not Entered
              </Badge>
            );
          }
          if (status === "Needs Validation") {
            return (
              <Badge size="sm" color="blue">
                Needs Validation
              </Badge>
            );
          }
          if (status === "Pending Validation") {
            return (
              <Badge size="sm" color="yellow">
                Pending Validation
              </Badge>
            );
          }

          return (
            <Badge size="sm" color="green">
              Validated
            </Badge>
          );
        },
      },
      {
        colId: "result",
        headerName: "Result",
        minWidth: 120,
        editable: isEditable,
        cellEditorSelector: (params) => {
          const data = params.data;
          if (!data) return { component: "agTextCellEditor" };

          const isValidation = data._userMode === "canValidate";

          if (data.testResultSpec.type === "TEXT") {
            const resultOptions = data.testResultSpec.testResults.map(
              (t) => t.testResult,
            );
            return {
              component: "agRichSelectCellEditor",
              params: {
                values: resultOptions,
                formatValue: (value) => value?.description,
                parseValue: (value) => value?.description,
                searchType: "match",
                // Explicitly set value to null for validation to prevent pre-selection
                value: isValidation ? null : undefined,
              } as IRichCellEditorParams,
            };
          }
          if (data.testResultSpec.type === "NUMERIC") {
            return {
              component: "agNumberCellEditor",
              params: {
                min: 0,
                max: 10,
                preventStepping: true,
              },
            };
          }
          if (data.testResultSpec.type === "ORGANISM") {
            return {
              component: "agRichSelectCellEditor",
              params: {
                values: organismResults,
                formatValue: (value) => value?.name,
                parseValue: (value) => value?.name,
                allowTyping: true,
                filterList: true,
                searchType: "match",
                value: isValidation ? "" : data.resultFreeText || "",
              } as IRichCellEditorParams,
            };
          }
          return {
            component: "agTextCellEditor",
            // For numeric/free text validation, start with empty value
            params: isValidation ? { value: "" } : undefined,
          };
        },
        valueGetter: resultValueGetter,
        valueFormatter: resultValueFormatter,
        valueSetter: resultValueSetter,
        cellClass: (params) => {
          if (!params.data) return "";

          const result = params.data?.testResultSpec.testResults.find(
            (t) => t.testResult.value === params.data?.resultId,
          );

          if (!result?.testResult.color) return "";

          switch (result.testResult.color) {
            case "red":
              return "text-red-800 bg-red-50 border-l-4 border-red-300 font-medium";
            case "green":
              return "text-green-800 bg-green-50 border-l-4 border-green-300 font-medium";
            case "yellow":
              return "text-yellow-800 bg-yellow-50 border-l-4 border-yellow-300 font-medium";
            case "blue":
              return "text-blue-800 bg-blue-50 border-l-4 border-blue-300 font-medium";
            case "purple":
              return "text-purple-800 bg-purple-50 border-l-4 border-purple-300 font-medium";
            case "orange":
              return "text-orange-800 bg-orange-50 border-l-4 border-orange-300 font-medium";
            case "gray":
              return "text-gray-800 bg-gray-50 border-l-4 border-gray-300 font-medium";
            default:
              return "";
          }
        },
      },
      ...attributeColumnDefs,
      {
        field: "comment",
        headerName: "Comment",
        editable: isCommentEditable,
        cellEditor: "agTextCellEditor",
      },
      {
        colId: "entered",
        headerName: "Entered",
        hide: true,
        valueGetter: (params) => {
          const data = params.data;
          if (!data?.enteredBy) return null;
          return `${data.enteredBy.username}${data.enteredAt ? ` @ ${format(data.enteredAt)}` : ""}`;
        },
        cellClass: "text-muted-foreground italic text-xs",
      },
      {
        colId: "validated",
        headerName: "Validated",
        hide: true,
        valueGetter: (params) => {
          const data = params.data;
          if (!data?.validatedBy) return null;
          return `${data.validatedBy.username}${data.validatedAt ? ` @ ${format(data.validatedAt)}` : ""}`;
        },
        cellClass: "text-muted-foreground italic text-xs",
      },
      // {
      //   colId: "action",
      //   headerName: "",
      //   width: 100,
      //   pinned: "right",
      //   cellRenderer: (params) => {
      //     return (
      //       <Button size="sm" className="h-5" variant="outline">
      //         Submit
      //       </Button>
      //     );
      //   },
      // },
    ] as ColDef<EnhancedWorkspaceObservationSummary>[];
  }, [
    resultValueGetter,
    resultValueSetter,
    resultValueFormatter,
    isEditable,
    isCommentEditable,
    attributeColumnDefs,
    organismResults,
  ]);

  return (
    <>
      <div className="flex-1 ag-theme-blis min-h-0">
        <AgGridReact
          ref={gridRef}
          columnDefs={colDefs}
          rowData={rowData}
          loading={isLoading}
          onCellValueChanged={handleCellValueChanged}
          theme={workspaceGridTheme}
          getRowId={(params) => params.data.id.toString()}
          autoSizeStrategy={{ type: "fitCellContents" }}
          enterNavigatesVerticallyAfterEdit
          cellSelection
          getRowClass={(params) => {
            const data = params.data;
            if (!data) return "";
            if (data._userMode === "cancelled") {
              return "!bg-accent-muted";
            }
            return "";
          }}
        />
      </div>
      <div className="px-6 py-2 border-t h-20 flex items-center justify-end gap-4">
        {changedIds.size > 0 && (
          <Text muted>
            {changedIds.size} pending {pluralize("change", changedIds.size)}
          </Text>
        )}
        <Button
          isDisabled={changedIds.size === 0 || submitInterpretation.isPending}
          isPending={submitInterpretation.isPending}
          onPress={handleSave}
        >
          Save
        </Button>
      </div>
      <ConflictWarningDialog
        isOpen={conflictDialogOpen}
        onOpenChange={setConflictDialogOpen}
        conflicts={conflicts}
      />
      <AccessionDialogProvider>
        <WorkflowResultEntryDetail
          row={rowData.find((r) => r.id === rowId) || null}
          onOpenChange={(open) => setRowId(open ? rowId : null)}
        />
        <UpdateResultDialog />
      </AccessionDialogProvider>
    </>
  );
}

function pluralize(word: string, count: number, suffix = "s") {
  return count === 1 ? word : `${word}${suffix}`;
}

function ConflictWarningDialog(props: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: Array<{
    accessionId: number;
    resultName: string | null;
    enteredValue: string;
    expectedValue: string;
  }>;
}) {
  return (
    <Modal isOpen={props.isOpen} onOpenChange={props.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Result Validation Conflicts</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <Text>
              The following results do not match the original entries. Please
              review and correct them:
            </Text>
            <div className="space-y-3">
              {props.conflicts.map((conflict, index) => (
                <div
                  key={index}
                  className="p-3 border rounded-lg bg-red-50 dark:bg-red-950/20"
                >
                  <div className="font-medium text-sm">
                    Accession: {conflict.accessionId} - {conflict.resultName}
                  </div>
                  <div className="mt-2 text-sm space-y-1">
                    <div>
                      Your entry:{" "}
                      <span className="font-mono bg-red-100 dark:bg-red-900/30 px-1 rounded">
                        {conflict.enteredValue || "(empty)"}
                      </span>
                    </div>
                    <div>
                      Expected:{" "}
                      <span className="font-mono bg-green-100 dark:bg-green-900/30 px-1 rounded">
                        {conflict.expectedValue || "(empty)"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogCloseButton>OK</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function WorkflowResultEntryDetail({
  row,
  onOpenChange,
}: {
  row: EnhancedWorkspaceObservationSummary | null;
  onOpenChange: (open: boolean) => void;
}) {
  const resultValue = React.useMemo(() => {
    if (!row) return null;
    const type = row.testResultSpec.type;
    if (type === "TEXT") {
      return row.testResultSpec.testResults.find(
        (t) => t.testResult.value === row.resultId,
      )?.testResult.description;
    }
    if (type === "NUMERIC") {
      return row.resultNumeric?.toString();
    }
    if (type === "FREE_TEXT" || type === "ORGANISM") {
      return row.resultFreeText;
    }
    return null;
  }, [row]);

  const attributes = React.useMemo(() => {
    if (!row) return null;
    return row.attributes.map((a) => ({
      label: a.attributeSpec.name,
      value:
        a.attributeSpec.type === "TEXT"
          ? a.textValue
          : a.numericValue?.toString(),
    }));
  }, [row]);

  return (
    <Sheet
      side="right"
      isOpen={!!row}
      onOpenChange={onOpenChange}
      classNames={{
        content: "md:max-w-lg",
      }}
    >
      <Dialog>
        <DialogHeader>
          <DialogTitle>Result Details</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {row && (
            <>
              {row._userMode === "cancelled" && (
                <Note intent="warning">
                  <p className="mb-2">This test has been cancelled.</p>
                  {(row.cancelledReason || row.testOrder.cancelledReason) && (
                    <>
                      <p>
                        <strong>Reason:</strong>
                      </p>
                      <p>
                        {row.cancelledReason || row.testOrder.cancelledReason}
                      </p>
                    </>
                  )}
                </Note>
              )}
              <DataList>
                <DataListItem>
                  <DataListLabel>Accession</DataListLabel>
                  <DataListValue>
                    <Link
                      to="/accessions/$id"
                      params={{ id: row.testOrder.accessionId }}
                    >
                      {row.testOrder.accessionId}
                    </Link>
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Sample</DataListLabel>
                  <DataListValue>
                    {getResultSample(row, row.testOrder)?.displayId || ""}
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Media</DataListLabel>
                  <DataListValue>
                    {row.materialLot?.materialSpec.name}
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Name</DataListLabel>
                  <DataListValue>{row.testResultSpec.name}</DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Result</DataListLabel>
                  <DataListValue>{resultValue}</DataListValue>
                </DataListItem>
                {attributes && (
                  <DataList>
                    {attributes.map((a) => (
                      <DataListItem key={a.label}>
                        <DataListLabel>{a.label}</DataListLabel>
                        <DataListValue>{a.value}</DataListValue>
                      </DataListItem>
                    ))}
                  </DataList>
                )}
                <DataListItem>
                  <DataListLabel>Status</DataListLabel>
                  <DataListValue>{row.status}</DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Comment</DataListLabel>
                  <DataListValue>{row.comment}</DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Entered</DataListLabel>
                  {row.enteredBy ? (
                    <DataListValue>
                      {row.enteredBy.username} @ {format(row.enteredAt)}
                    </DataListValue>
                  ) : (
                    <DataListValue />
                  )}
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Validated</DataListLabel>
                  {row.validatedBy ? (
                    <DataListValue>
                      {row.validatedBy.username} @ {format(row.validatedAt)}
                    </DataListValue>
                  ) : (
                    <DataListValue />
                  )}
                </DataListItem>
                {row._userMode === "cancelled" && (
                  <DataListItem>
                    <DataListLabel>Cancelled</DataListLabel>
                    <DataListValue>
                      {(row.cancelledBy ?? row.testOrder.cancelledBy)
                        ?.username ?? "Unknown"}
                      {(row.cancelledAt ?? row.testOrder.cancelledAt) &&
                        ` @ ${format(row.cancelledAt ?? row.testOrder.cancelledAt)}`}
                    </DataListValue>
                  </DataListItem>
                )}
              </DataList>
              <ResultEntryActions row={row} />
            </>
          )}
        </DialogBody>
      </Dialog>
    </Sheet>
  );
}

function hasEmpty(data: WorkspaceResultEntryPayload | undefined) {
  if (!data) return false;
  return data.testOrderResults.some((r) => r.status === "empty");
}

// function PrintLabelsButton({
//   testRunId,
//   results,
// }: {
//   testRunId?: number;
//   results: WorkspaceResultEntry[];
// }) {
//   const { instanceMaterials } = useWorkspace();
//   const resultSamples = results
//     .map((r) => getResultSample(r, r.testOrder))
//     .filter((s) => s !== null);
//   const [isPrinting, setIsPrinting] = React.useState(false);
//   const { printMaterialLabels } = usePrintLabel(testRunId);

//   const onPrintLabels = React.useCallback(async () => {
//     try {
//       setIsPrinting(true);
//       const samples = await apiUtils.samples.forLabels.fetch(
//         {
//           ids: resultSamples.map((sample) => sample.id),
//         },
//         { retry: false },
//       );
//       await printMaterialLabels(
//         samples,
//         instanceMaterials.map((material) => ({
//           code: material.materialSpec.code,
//           lotNumber: material.lotNumber,
//           expiration: format(material.expiration, { time: false }),
//         })),
//       );
//     } catch (error) {
//       toast.error("Failed to print labels");
//     } finally {
//       setIsPrinting(false);
//     }
//   }, [resultSamples, instanceMaterials, printMaterialLabels]);

//   if (instanceMaterials.length === 0) {
//     return null;
//   }

//   return (
//     <Button variant="outline" onPress={onPrintLabels} isPending={isPrinting}>
//       <PrinterIcon />
//       Print labels
//     </Button>
//   );
// }

function ResultEntryActions({
  row,
}: {
  row: EnhancedWorkspaceObservationSummary;
}) {
  const setDialogState = useSetDialogState("updateTestResult");
  const { instanceMaterials } = useWorkspace();
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  const [showCreateConfirm, setShowCreateConfirm] = React.useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = React.useState(false);
  const { printMaterialLabels } = usePrintLabel();

  const clearResult = trpc.results.clearV2.useMutation(
    mutationResponse({
      errorTitle: "Failed to clear result",
      successMessage: "Result cleared",
      onSuccess: () => {
        setShowClearConfirm(false);
      },
    }),
  );

  const createResult = trpc.results.createResult.useMutation(
    mutationResponse({
      errorTitle: "Failed to create result",
      successMessage: "Result created",
      onSuccess: () => {
        setShowCreateConfirm(false);
      },
    }),
  );

  const restoreResult = trpc.results.restore.useMutation(
    mutationResponse({
      errorTitle: "Failed to restore result",
      successMessage: "Result restored",
      onSuccess: () => {
        setShowRestoreConfirm(false);
      },
    }),
  );

  const onPrintLabels = React.useCallback(async () => {
    if (!row) return;

    try {
      setIsPrinting(true);
      const sample = getResultSample(row, row.testOrder);
      if (!sample) return;

      const samples = await apiUtils.samples.forLabels.fetch(
        { ids: [sample.id] },
        { retry: false },
      );
      await printMaterialLabels(
        samples,
        instanceMaterials.map((material) => ({
          code: material.materialSpec.code,
          lotNumber: material.lotNumber,
          expiration: format(material.expiration, { time: false }),
        })),
      );
    } catch (error) {
      console.error("Failed to print labels:", error);
      // You might want to add toast notification here
    } finally {
      setIsPrinting(false);
    }
  }, [row, instanceMaterials, printMaterialLabels]);

  return (
    <>
      <CustomMenuTrigger>
        {/* <Button variant="outline" className="w-full">
                  Actions
                </Button> */}
        <Menu className="border rounded-md p-1 mt-2">
          {/* <MenuItem onAction={onPrintLabels} isDisabled={isPrinting}>
            <PrinterIcon />
            Print labels
          </MenuItem>
          <MenuSeparator /> */}
          <MenuItem
            onAction={() => setDialogState({ resultId: row.id })}
            isDisabled={row._userMode === "cancelled" || row.status === "empty"}
          >
            <PencilIcon />
            Update result
          </MenuItem>
          <MenuItem
            onAction={() => setShowCreateConfirm(true)}
            isDisabled={createResult.isPending}
          >
            <PlusIcon />
            Add result
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            isDanger
            onAction={() => setShowClearConfirm(true)}
            isDisabled={
              clearResult.isPending ||
              row.status === "empty" ||
              row._userMode === "cancelled"
            }
          >
            <XSquareIcon />
            Clear result
          </MenuItem>
          {row._userMode === "cancelled" ? (
            <MenuItem
              onAction={() => setShowRestoreConfirm(true)}
              isDisabled={restoreResult.isPending}
            >
              <FileUpIcon />
              Restore result
            </MenuItem>
          ) : (
            <MenuItem isDanger onAction={() => setShowCancelConfirm(true)}>
              <CircleSlashIcon />
              Cancel result
            </MenuItem>
          )}
        </Menu>
      </CustomMenuTrigger>

      <ConfirmPrompt
        open={showClearConfirm}
        onOpenChange={setShowClearConfirm}
        onConfirm={(reason) =>
          clearResult.mutate({
            id: row.id,
            // reason should be enforced by ConfirmPrompt
            reason: reason || "Unknown",
          })
        }
        message="Are you sure you want to clear this result?"
        danger
        title="Confirm Clear Result"
        warningText="This action cannot be undone."
        requireReason
        loading={clearResult.isPending}
      />

      <ConfirmPrompt
        open={showCreateConfirm}
        onOpenChange={setShowCreateConfirm}
        onConfirm={() => {
          const sample = getResultSample(row, row.testOrder);
          if (sample) {
            createResult.mutate({
              testOrderId: row.testOrderId,
              testResultSpecId: row.testResultSpecId,
              sampleId: sample.id,
              accessionId: row.testOrder.accessionId,
              materialLotId: row.materialLotId,
              isReflex: row.isReflex,
              parentResultId: row.parentResultId,
            });
          }
        }}
        message="Are you sure you want to create a new result?"
        title="Confirm Add Result"
        warningText="This will create a new result entry row for this test."
        loading={createResult.isPending}
      />

      <ConfirmPrompt
        open={showRestoreConfirm}
        onOpenChange={setShowRestoreConfirm}
        onConfirm={() => {
          restoreResult.mutate({ id: row.id });
        }}
        message="Are you sure you want to restore this result?"
        title="Confirm Restore Result"
        loading={restoreResult.isPending}
      />

      <CancelResultDialog
        row={row}
        isOpen={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
      />
    </>
  );
}

function CancelResultDialog({
  row,
  isOpen,
  onOpenChange,
}: {
  row: EnhancedWorkspaceObservationSummary;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const cancelResult = trpc.results.cancel.useMutation(
    mutationResponse({
      errorTitle: "Failed to cancel result",
      successMessage: "Result cancelled",
      onSuccess: () => {
        onOpenChange(false);
      },
    }),
  );

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const reason = formData.get("reason") as string;
    cancelResult.mutate({ id: row.id, reason });
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Cancel result</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form onSubmit={onSubmit} id="cancel-form">
            <TextField name="reason" isRequired>
              <Label>Reason for cancelling</Label>
              <TextArea />
              <FieldError />
            </TextField>
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="submit"
            form="cancel-form"
            isPending={cancelResult.isPending}
            isDisabled={cancelResult.isPending}
          >
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
